import React from 'react'
import { Form, Input, Select, DatePicker } from '@surecompanies/core_components'
import dayjs from 'dayjs'
import { FieldGroup } from '../../PaymentDetailModal/PaymentDetailModal.styles'
import {
  StatusUpdateFormKey,
  StatusUpdateFormKeyLabels,
} from '../statusUpdate.type'
import { PaymentTypeEnum } from 'types/benefit'

const AUTOPAY_PULL_DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1)

interface PaymentEvidenceFieldsProps {
  primaryPaymentMethod?: string
}

export function PaymentEvidenceFields({
  primaryPaymentMethod,
}: PaymentEvidenceFieldsProps) {
  const today = dayjs()

  const isGroupAch = primaryPaymentMethod === PaymentTypeEnum.GROUP_ACH
  const isCheck = primaryPaymentMethod === PaymentTypeEnum.CHECK
  return (
    <>
      <FieldGroup>
        <Form.Item
          name={StatusUpdateFormKey.ACTUAL_PREMIUM_PAID}
          label={
            StatusUpdateFormKeyLabels[StatusUpdateFormKey.ACTUAL_PREMIUM_PAID]
          }
          rules={[
            {
              required: true,
              message: `${StatusUpdateFormKeyLabels[StatusUpdateFormKey.ACTUAL_PREMIUM_PAID]} is required`,
            },
          ]}
          style={{ marginBottom: 0 }}
        >
          <Input
            type="number"
            placeholder="Enter amount"
            prefix="$"
            style={{ width: '100%' }}
          />
        </Form.Item>
      </FieldGroup>

      <FieldGroup>
        <Form.Item
          name={StatusUpdateFormKey.ACTUAL_PAYMENT_DATE}
          label={
            StatusUpdateFormKeyLabels[StatusUpdateFormKey.ACTUAL_PAYMENT_DATE]
          }
          initialValue={today}
          rules={[
            {
              required: true,
              message: `${StatusUpdateFormKeyLabels[StatusUpdateFormKey.ACTUAL_PAYMENT_DATE]} is required`,
            },
          ]}
          style={{ marginBottom: 0 }}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="MM-DD-YYYY"
            placeholder="Select date"
          />
        </Form.Item>
      </FieldGroup>

      {!isGroupAch && !isCheck && (
        <FieldGroup>
          <Form.Item
            name={StatusUpdateFormKey.CONFIRMATION_CODE}
            label={
              StatusUpdateFormKeyLabels[StatusUpdateFormKey.CONFIRMATION_CODE]
            }
            rules={
              !isGroupAch && !isCheck
                ? [
                    {
                      required: true,
                      message: `${StatusUpdateFormKeyLabels[StatusUpdateFormKey.CONFIRMATION_CODE]} is required`,
                    },
                  ]
                : undefined
            }
            style={{ marginBottom: 0 }}
          >
            <Input
              placeholder={
                !isGroupAch && !isCheck
                  ? 'Enter confirmation code (required)'
                  : 'Enter confirmation code (optional)'
              }
              style={{ width: '100%' }}
            />
          </Form.Item>
        </FieldGroup>
      )}

      {isCheck && (
        <FieldGroup>
          <Form.Item
            name={StatusUpdateFormKey.CHECK_NUMBER}
            label={StatusUpdateFormKeyLabels[StatusUpdateFormKey.CHECK_NUMBER]}
            rules={[
              {
                required: true,
                message: `${StatusUpdateFormKeyLabels[StatusUpdateFormKey.CHECK_NUMBER]} is required`,
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input placeholder="Enter check number" style={{ width: '100%' }} />
          </Form.Item>
        </FieldGroup>
      )}

      {/* Hide Autopay fields for CHECK and GROUP_ACH - default to false in payload */}
      {!isCheck && !isGroupAch && (
        <>
          <FieldGroup style={{ gridColumn: '1 / -1' }}>
            <Form.Item
              name={StatusUpdateFormKey.DID_SET_UP_AUTOPAY}
              label={
                StatusUpdateFormKeyLabels[
                  StatusUpdateFormKey.DID_SET_UP_AUTOPAY
                ]
              }
              rules={[
                {
                  required: true,
                  message: `${StatusUpdateFormKeyLabels[StatusUpdateFormKey.DID_SET_UP_AUTOPAY]} is required`,
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <Select placeholder="Select yes or no" style={{ width: '100%' }}>
                <Select.Option value={true}>Yes</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>
          </FieldGroup>

          <Form.Item
            noStyle
            shouldUpdate={(prev: any, curr: any) =>
              prev?.[StatusUpdateFormKey.DID_SET_UP_AUTOPAY] !==
              curr?.[StatusUpdateFormKey.DID_SET_UP_AUTOPAY]
            }
          >
            {({ getFieldValue }) => {
              const didSetUpAutopay = getFieldValue(
                StatusUpdateFormKey.DID_SET_UP_AUTOPAY
              )
              return didSetUpAutopay === true ? (
                <>
                  <FieldGroup style={{ gridColumn: '1 / -1' }}>
                    <Form.Item
                      name={StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD}
                      label={
                        StatusUpdateFormKeyLabels[
                          StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD
                        ]
                      }
                      rules={[
                        {
                          required: true,
                          message: `${StatusUpdateFormKeyLabels[StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD]} is required`,
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="Select method"
                        style={{ width: '100%' }}
                      >
                        <Select.Option value="VCN">VCN</Select.Option>
                        <Select.Option value="ACH">ACH</Select.Option>
                      </Select>
                    </Form.Item>
                  </FieldGroup>
                  <FieldGroup>
                    <Form.Item
                      name={StatusUpdateFormKey.AUTOPAY_PULL_DAY}
                      label={
                        StatusUpdateFormKeyLabels[
                          StatusUpdateFormKey.AUTOPAY_PULL_DAY
                        ]
                      }
                      rules={[
                        {
                          required: true,
                          message: `${StatusUpdateFormKeyLabels[StatusUpdateFormKey.AUTOPAY_PULL_DAY]} is required`,
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="Select day"
                        style={{ width: '100%' }}
                        showSearch
                        optionFilterProp="children"
                      >
                        {AUTOPAY_PULL_DAY_OPTIONS.map((day) => (
                          <Select.Option key={day} value={day}>
                            {day}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </FieldGroup>
                </>
              ) : null
            }}
          </Form.Item>
        </>
      )}
    </>
  )
}
